import { useMemo, useState } from "react";
import { ShieldCheck, CheckCircle2, XCircle, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const fmtINR = (n) =>
  "₹" +
  Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// FOIR-based eligibility
// Max EMI = (net_income * FOIR) - existing_emi
// FOIR varies by income tier
const foirForIncome = (income) => {
  if (income < 25000) return 0.4;
  if (income < 50000) return 0.5;
  if (income < 100000) return 0.55;
  return 0.6;
};

const productConfig = {
  "home-loan": { label: "Home Loan", rate: 8.75, tenure: 20 },
  "personal-loan": { label: "Personal Loan", rate: 12.5, tenure: 5 },
  "business-loan": { label: "Business Loan", rate: 13.5, tenure: 5 },
  "mortgage-loan": { label: "Mortgage / LAP", rate: 10.25, tenure: 15 },
  "home-loan-doctor": { label: "Doctor / Professional", rate: 9.75, tenure: 10 },
};

const calcMaxLoan = (emi, annualRate, years) => {
  const n = years * 12;
  const r = annualRate / 12 / 100;
  if (r === 0) return emi * n;
  return (emi * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
};

export default function EligibilityChecker() {
  const [product, setProduct] = useState("home-loan");
  const [incomeStr, setIncomeStr] = useState("75000");
  const [existingEmiStr, setExistingEmiStr] = useState("");
  const [ageStr, setAgeStr] = useState("30");
  const [tenureStr, setTenureStr] = useState("");

  const income = Math.max(0, Number(incomeStr) || 0);
  const existingEmi = Math.max(0, Number(existingEmiStr) || 0);
  const age = Math.min(75, Math.max(18, Number(ageStr) || 18));

  const result = useMemo(() => {
    const cfg = productConfig[product];
    const foir = foirForIncome(income);
    const maxEmi = Math.max(0, income * foir - existingEmi);
    const tenureCap = Math.min(cfg.tenure, Math.max(1, 60 - age));
    const userTenure = Number(tenureStr) || 0;
    const tenure = userTenure > 0 ? Math.min(userTenure, tenureCap) : tenureCap;
    const maxLoan = calcMaxLoan(maxEmi, cfg.rate, tenure);
    const eligible = maxLoan >= 50000 && age >= 21 && age <= 65;
    return {
      maxEmi,
      maxLoan,
      tenure,
      tenureCap,
      rate: cfg.rate,
      foir,
      eligible,
      cfg,
    };
  }, [product, income, existingEmi, age, tenureStr]);

  const inputCls =
    "w-full bg-[#1a1a1a] border border-white/10 text-white placeholder-neutral-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] transition-colors";
  const labelCls =
    "block text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono";

  return (
    <div
      data-testid="eligibility-checker"
      className="border border-white/10 rounded-2xl bg-[#0f0f0f] p-5 sm:p-10"
    >
      <div className="flex items-center gap-3 mb-6 sm:mb-8">
        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-lg bg-[#1d4ed8]/10 border border-[#1d4ed8]/20 flex items-center justify-center text-[#1d4ed8] flex-shrink-0">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-[#1d4ed8]">
            Eligibility Checker
          </div>
          <h3 className="font-display font-bold text-lg sm:text-2xl text-white tracking-tight">
            Check how much you can borrow
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8">
        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className={labelCls}>Loan Product</label>
            <select
              className={inputCls}
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              data-testid="elig-product"
            >
              {Object.entries(productConfig).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Monthly Income (₹)</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className={inputCls}
              value={incomeStr}
              onChange={(e) => setIncomeStr(e.target.value)}
              placeholder="e.g. 75000"
              data-testid="elig-income"
            />
          </div>
          <div>
            <label className={labelCls}>Existing EMI (₹)</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              className={inputCls}
              value={existingEmiStr}
              onChange={(e) => setExistingEmiStr(e.target.value)}
              placeholder="0"
              data-testid="elig-existing-emi"
            />
          </div>
          <div>
            <label className={labelCls}>Your Age</label>
            <input
              type="number"
              inputMode="numeric"
              min={18}
              max={75}
              className={inputCls}
              value={ageStr}
              onChange={(e) => setAgeStr(e.target.value)}
              placeholder="e.g. 30"
              data-testid="elig-age"
            />
          </div>
          <div>
            <label className={labelCls}>
              Tenure (years)
              <span className="text-neutral-600 normal-case tracking-normal ml-2 font-sans">
                max {result.tenureCap}
              </span>
            </label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={result.tenureCap}
              className={inputCls}
              value={tenureStr}
              onChange={(e) => setTenureStr(e.target.value)}
              placeholder={`Auto (${result.tenureCap})`}
              data-testid="elig-tenure"
            />
          </div>
          <div>
            <label className={labelCls}>Indicative Rate</label>
            <div className={`${inputCls} bg-[#121212]`}>
              <span className="font-mono text-white">{result.rate.toFixed(2)}%</span>
              <span className="text-neutral-500 ml-2 text-xs">
                · {result.tenure} yrs
              </span>
            </div>
          </div>
        </div>

        <div
          className={`lg:col-span-2 rounded-xl border p-6 flex flex-col justify-between ${
            result.eligible
              ? "border-[#1d4ed8]/30 bg-gradient-to-br from-[#1d4ed8]/10 via-transparent to-[#dc2626]/10"
              : "border-[#dc2626]/30 bg-[#dc2626]/5"
          }`}
        >
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-neutral-400">
              {result.eligible ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Eligible
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-[#dc2626]" /> Not eligible
                </>
              )}
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500 mt-4">
              You may qualify for up to
            </div>
            <div
              data-testid="elig-max-loan"
              className="mt-2 font-display font-bold text-3xl sm:text-5xl text-white tracking-tighter"
            >
              {fmtINR(result.maxLoan)}
            </div>

            <div className="mt-6 space-y-2.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Max EMI</span>
                <span data-testid="elig-max-emi" className="font-mono text-white">
                  {fmtINR(result.maxEmi)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Tenure</span>
                <span className="font-mono text-white">
                  {result.tenure} years
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">FOIR applied</span>
                <span className="font-mono text-white">
                  {(result.foir * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>

          <Link
            to="/request-access"
            data-testid="elig-apply-cta"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-5 py-3 font-semibold"
          >
            Apply Now <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <p className="mt-6 text-xs text-neutral-500 font-mono">
        * Indicative estimate using FOIR-based methodology. Final eligibility is
        at lender's discretion and depends on credit score, employment, co-applicant
        income and other factors.
      </p>
    </div>
  );
}
